import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { CustomRouteComponentProps } from 'react-router-dom';
import { useLocation } from 'react-router-dom-v5-compat';

import type { W_Material_CreateBulk_RM, W_Material_CreateBulk_RM_Single } from 'ab-api/web/material/createBulk';

import type { BulkCreateResponseViewModel } from 'ab-viewModels/csv.viewModel';

import type { RootState } from 'af-reducers';

import { MATERIAL_CSV_SAMPLE } from 'af-constants/csvSamples';
import CLIENT from 'af-constants/routes/client';

import BulkImport from 'af-components/BulkUpsert/Import';
import CodeField from 'af-components/CodeField';

import * as MaterialActions from 'af-actions/material';

import { parse } from './parse';

type Props = CustomRouteComponentProps & ConnectedProps<typeof connector>;

const MANDATORY_FIELDS = [
	'group1',
	'displayName',
	'unit',
	'baseCost',
];

const MaterialsBulkUpload: React.FC<Props> = (props) => {
	const { createMaterials, companyName } = props;

	const { state: { orgAlias } } = useLocation();

	const manual = React.useMemo(() => {
		return [
			'Import multiple materials to your company using a CSV upload feature.',
			'Fields that should be located in the CSV are listed below.',
			'The ones marked with an asterisk are required and should not be left out.',
			'Fields marked with a question mark are optional.',
			'After uploading data, it will get validated. Rows that have incorrect or conflicting data will be ignored.',
		];
	}, []);

	const fields = React.useMemo(() => {
		return (
			<CodeField isBlue={true}>
				<b>group1</b>                               | * | The main material group<br />
				<b>group2</b>                               |   | An additional material group<br />
				<b>group3</b>                               |   | An additional material group<br />
				<b>vendor</b>                               |   | Vendor Name<br />
				<b>vendorId</b>                             |   | ID unique for a given Vendor<br />
				<b>displayName</b>                          | * | Display Name<br />
				<b>description</b>                          |   | Description<br />
				<b>unit</b>                                 | * | Unit<br />
				<b>baseCost</b>                             | * | Base Cost<br />
				<b>shippingMarkup</b>                       |   | Shipping<br />
				<b>salesMarkup</b>                          |   | Sales Markup<br />
				<b>tax</b>                                  |   | Tax<br />
			</CodeField>
		);
	}, []);

	const importBatch = React.useCallback(
		async (data: W_Material_CreateBulk_RM
		): Promise<void | BulkCreateResponseViewModel<W_Material_CreateBulk_RM_Single>> => {

			if (!data.materials.length) {
				return {
					error: {
						uniqueId: 'No data provided.',
					},
					rejected: undefined,
					importedCount: 0,
					updatedCount: 0,
				};
			}

			return await createMaterials(data);
		}, [createMaterials]);

	return (
		<BulkImport
			fields={fields}
			importBatch={importBatch}
			listRoute={CLIENT.COMPANY.RESOURCES.MATERIAL.LIST(orgAlias, companyName)}
			mandatoryFields={MANDATORY_FIELDS}
			manual={manual}
			parse={parse}
			sampleData={MATERIAL_CSV_SAMPLE}
			title="Materials"
		/>
	);
};

function mapStateToProps(state: RootState) {
	const { companyData } = state.user;

	if (!companyData) {
		throw new Error('User not logged in');
	}

	return {
		companyName: companyData.name,
	};
}

function mapDispatchToProps() {
	return {
		createMaterials: MaterialActions.createMaterialsBulk,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

export default connector(MaterialsBulkUpload);
